import { useEffect, useState } from 'react';
import { useCustomers } from './useCustomers';
import { useActiveVehicle } from '../context/ActiveVehicleProvider';
import { useNotificationProvider } from 'hooks';
import { ParkingLocation } from '../types/parkingLocation';
import {
	readScanLastSelection,
	writeScanLastSelection,
} from '../utils/scanLastSelection';

export const useCANparkHome = () => {
	const [activeParkingLocation, setActiveParkingLocation] =
		useState<ParkingLocation | null>(null);
	const [selectedState, setSelectedState] = useState<string | null>('CA');

	const [licensePlateNumber, setLicensePlateNumber] = useState<string>('');
	const [capturedImage, setCapturedImage] = useState<string | null>(null);
	const [selectedCustomer, setSelectedCustomer] = useState<{
		Id: string;
		Name: string;
	} | null>(null);

	const { customers, loading: loadingCustomers } = useCustomers();

	const { setCurrentSearch, currentSearch } = useActiveVehicle();

	const { notifyError } = useNotificationProvider();

	const [isCameraOn, setIsCameraOn] = useState(false);

	// Pick an initial customer once the list arrives. Prefer the last-used
	// customer from localStorage so operators land on the same lot they were
	// scanning yesterday; fall back to the first available customer.
	useEffect(() => {
		if (customers.length > 0) {
			const savedId = readScanLastSelection().customerId;
			const match = savedId
				? customers.find((c) => c.Id === savedId)
				: undefined;
			setSelectedCustomer(match ?? customers[0]);
		}
	}, [customers]);

	// Persist the selected customer and clear the active lot when the
	// customer changes. The lot selector restores its own last-used value
	// from storage once its options finish loading.
	useEffect(() => {
		setActiveParkingLocation(null);
		if (selectedCustomer) {
			writeScanLastSelection({ customerId: selectedCustomer.Id });
		}
	}, [selectedCustomer]);

	// Reset fields when a new search is started
	useEffect(() => {
		if (!currentSearch) {
			setLicensePlateNumber('');
			setSelectedState('CA');
		}
	}, [currentSearch]);

	const handleViewLicenseHistoryClick = () => {
		if (!activeParkingLocation || !licensePlateNumber || !selectedState) {
			notifyError('Please fill in all fields');
			return;
		}

		setCurrentSearch({
			parkingLocation: activeParkingLocation,
			licenseNumber: licensePlateNumber,
			licenseState: selectedState,
		});
		// Clear the scan preview once we've handed off to the ticket flow so
		// the entry tab doesn't show a stale preview when the drawer closes.
		setCapturedImage(null);
	};

	// Called from useCamera after a clean OCR when the lot has
	// showScanPreview disabled (skip the confirm preview). Takes plate/state
	// as args rather than reading from state because the state updates from
	// the same scan may not have committed yet when this fires.
	const handleAutoAdvanceFromScan = (plate: string, state: string) => {
		if (!activeParkingLocation || !plate || !state) return;
		setCurrentSearch({
			parkingLocation: activeParkingLocation,
			licenseNumber: plate,
			licenseState: state,
		});
		setCapturedImage(null);
	};

	return {
		activeParkingLocation,
		setActiveParkingLocation,
		selectedState,
		setSelectedState,
		licensePlateNumber,
		setLicensePlateNumber,
		selectedCustomer,
		setSelectedCustomer,
		customers,
		loadingCustomers,
		isCameraOn,
		setIsCameraOn,
		capturedImage,
		setCapturedImage,
		handleViewLicenseHistoryClick,
		handleAutoAdvanceFromScan,
	};
};
